import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { toast } from "@/components/ui/UseToast";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Button } from "@/components/ui/Button";
import { Separator } from "@/components/ui/Separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";
import { InputTags } from "@/components/ui/input-tags/Tags";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import { Check, ChevronDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGetAllCounterQuery, useGetCounterListQuery } from "@/services/CounterApi";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import { useDebounce } from "@/hooks/UseDebounce";
import PhoneInputField from "@/components/custom-component/PhoneInputField";
import { useLazyGetUserByUsernameQuery } from "@/services/UserApi";
import { Badge } from "@/components/ui/Badge";
import { useGenerateUserTicketFromServiceMutation } from "@/services/SelfServiceKiosk";
import { ErrorType } from "@/services/AuthService";
import { TicketActionOnStatus } from "@/services/LiveQueueService";
import { status } from "@/constants/GlobalFilter";
import {
  flattenServiceTags,
  groupServicesByDepartment,
} from "./tag-helpers";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { useGetTagsQuery } from "@/services/TagsApi";
import { GroupSearchDropdown } from "@/components/ui/group-search-dropdown";
import { Event } from "@/app/types/event";
import { PathDropdown } from "@/components/ui/PathDropdown";
import { createTagTreeFromTagNodes } from "@/utils/Helper";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

interface AddTicketFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
  initialDepartment?: number;
  initialService?: number;
  initialTitle?: string;
  initialCounter?: number;
  event?: Event;
}

interface TicketFormValues {
  department_id: number | string;
  service_id: number | string;
  title: string;
  description?: string;
  id?:string;
  customer_its?: string;
  customer_name?: string;
  phone_number?: string;
  email?: string;
  counter_id: number | string;
  status: string;
  assigned_to?: number | string;
  appointment_date: string;
  appointment_time: string;
  tag_id?: number | null;
  path?: string | null;
}

const initialValues: TicketFormValues = {
  department_id: "",
  service_id: "",
  title: "",
  description: "",
  id: "",
  customer_its: "",
  customer_name: "",
  phone_number: "",
  email: "",
  counter_id: "",
  status: Object.values(TicketActionOnStatus)[0], // Use first value from enum
  assigned_to: "",
  appointment_date: formatInTimeZone(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone, "yyyy-MM-dd"),
  appointment_time: `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`,
  tag_id: null,
  path: null,
};


const AddTicketForm: React.FC<AddTicketFormProps> = ({
  isOpen,
  setOpen,
  refetch,
  initialDepartment,
  initialService,
  initialTitle,
  initialCounter,
  event,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<
    string | undefined
  >(initialDepartment ? String(initialDepartment) : undefined);
  const [selectedService, setSelectedService] = useState<string | undefined>(
    initialService ? String(initialService) : undefined
  );
  const [itsSearchTerm, setItsSearchTerm] = useState("");
  const debouncedItsSearch = useDebounce(itsSearchTerm, 500);
  const [itsUserResults, setItsUserResults] = useState<any[]>([]);
  const isResettingService = useRef(false);
  const [titleInput, setTitleInput] = useState(initialTitle || "");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [isTitlePopoverOpen, setIsTitlePopoverOpen] = useState(false);

  // Add this to fetch users by ITS
  const [getUserByUsername] = useLazyGetUserByUsernameQuery();

  const { eventId: paramEventId } = useParams();
  const effectiveEventId = event?.event_id || (paramEventId ? parseInt(paramEventId) : undefined);

  const { userInfo } = useSelector((state: RootState) => state.auth);
  const userOrgId = Number(userInfo?.org_id);
  
  const { data: tagsData } = useGetTagsQuery(
    { org_id: userOrgId, event_ids: effectiveEventId ? [effectiveEventId] : undefined }, 
    { skip: !isOpen }
  );

  const allTags = tagsData?.data ?? [];

  // Form handling
  const form = useForm<TicketFormValues>({
    mode: "all",
    defaultValues: {
      ...initialValues,
      department_id: initialDepartment || "",
      service_id: initialService || "",
      title: initialTitle || "",
      counter_id: initialCounter || "",
      assigned_to: userInfo?.id || "",
      appointment_date: formatInTimeZone(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone, "yyyy-MM-dd"),
      appointment_time: `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
    clearErrors,
  } = form;

  // Watch form values
  const watchedDepartment = watch("department_id");
  const watchedService = watch("service_id");
  const watchedIts = watch("customer_its");

  // Update the counter query to include service_id in the query string
  const { data: countersData, isLoading: isCounterDataLoading } =
    useGetAllCounterQuery(
      {
        query: watchedService ? `service_id=${watchedService}` : "",
      },
      {
        skip: !watchedService, // Only fetch counters when service is selected
      }
    );
  const { data: staffData } = useGetStaffListQuery({ query: "" });

  const groupedTags = React.useMemo(
    () => (allTags.length > 0 ? createTagTreeFromTagNodes(allTags) : []),
    [allTags]
  );

  const tagOptions = React.useMemo(
    () => (allTags.length > 0 ? flattenServiceTags(allTags) : []),
    [allTags]
  );

  // Set up counter options
  const counterOptions = React.useMemo(
    () =>
      countersData?.data?.map((counter) => ({
        label: `${counter.code}`,
        value: counter.id,
      })) || [],
    [countersData?.data]
  );

  // Set up staff options
  const staffOptions = React.useMemo(
    () =>
      staffData?.map((staff) => ({
        label: staff.label,
        value: staff.value,
        designation:staff.designation
      })) || [],
    [staffData]
  );

  // Effect to update services when department changes
  useEffect(() => {
    if (watchedDepartment) {
      setSelectedDepartment(String(watchedDepartment));

      // Only clear service if this isn't the initial render with initialService
      if (!initialService) {
        isResettingService.current = true;
        setValue("service_id", "");
        setSelectedService(undefined);
        setValue("counter_id", "");
        // Reset the flag after state updates
        setTimeout(() => {
          isResettingService.current = false;
        }, 0);
      }
    }
  }, [watchedDepartment, initialService, setValue]);

  // New effect to update counters when service changes
  useEffect(() => {
    // Skip this effect if we're in the middle of resetting
    if (isResettingService.current) return;

    if (watchedService) {
      setSelectedService(String(watchedService));
      // Clear counter selection when service changes
      if (!initialCounter) {
        setValue("counter_id", "");
      }
    } else {
      setSelectedService(undefined);
    }
  }, [watchedService, initialCounter, setValue]);

  // Effect to search for ITS users
  useEffect(() => {
    const searchItsUsers = async () => {
      if (debouncedItsSearch && debouncedItsSearch.length >= 2) {
        try {
          // Call the real API instead of using mock data
          const response = await getUserByUsername({ username: debouncedItsSearch }).unwrap();
          setItsUserResults(response || []);
        } catch (error) {
          console.error("Error searching ITS users", error);
          setItsUserResults([]);
        }
      } else {
        setItsUserResults([]);
      }
    };

    searchItsUsers();
  }, [debouncedItsSearch, getUserByUsername]);

  // Handle form submission
  const [generateTicket, { isLoading: isGeneratingTicket }] = useGenerateUserTicketFromServiceMutation();

  const onSubmit = async (data: TicketFormValues) => {
    try {
      const now = new Date();
      const currentDate = formatInTimeZone(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone, "yyyy-MM-dd");
      const currentTimeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

      const finalDate = data.appointment_date || currentDate;
      const finalTimeRaw = data.appointment_time || currentTimeStr;

      // Format time as HH:MM:SS
      const formattedTime = finalTimeRaw.includes(":")
        ? `${finalTimeRaw}${finalTimeRaw.split(":").length < 3 ? ":00" : ""}`
        : `${finalTimeRaw}:00`;

      let eta = 20;
      if (finalDate === currentDate) {
        const appointmentTime = new Date(`${finalDate}T${formattedTime}`);

        // Calculate difference in minutes
        const diffInMinutes = Math.floor(
          (appointmentTime.getTime() - now.getTime()) / (1000 * 60)
        );

        // Only set ETA if difference is greater than 60 minutes
        eta = diffInMinutes > 60 ? diffInMinutes : 20;
      }
      // Format data for the API
      const formattedPath = data.path || undefined;

      const ticketData = {
        service_id: Number(data.service_id),
        title: data.title,
        date: finalDate,
        time: formattedTime,
        is_admin: true,
        description: data.description,
        user_id: data.customer_its ? Number(data.id) : undefined,
        eta: eta,
        counter: Number(data.counter_id),
        assigned_to: data.assigned_to ? String(data.assigned_to) : undefined,
        status: data.status,
        tag_id: data.tag_id || undefined,
        path: formattedPath,
        event_id: effectiveEventId,
      };

      // Call the mutation
      console.log("Submitting ticket data:", ticketData);
      const response = await generateTicket(ticketData).unwrap();
      
      toast({
        variant: "success", 
        title: "Success",
        description: `Ticket ${response.ticket_number} created successfully`,
      });

      handleClose();
      refetch();
      reset({ ...initialValues, assigned_to: userInfo?.id || "" }); // Reset form after successful submission
      setTitleInput("")
      setItsSearchTerm("")
    } catch (error: unknown) {
      console.error("Error creating ticket", error);
      const errorData = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: errorData?.data?.message || "An error occurred while creating the ticket",
      });
    }
  };

  // Handle validation errors
  const onError = (errors: any) => {
    console.error("Form errors:", errors);
    toast({
      variant: "destructive",
      description: "Please fill all the required fields correctly",
    });
  };

  // Handle user selection from ITS search
  const handleSelectItsUser = (user: any) => {
    setValue("id",user.id)
    setValue("customer_its", user.username);
    setValue("customer_name", `${user.first_name} ${user.last_name}`);
    setValue("phone_number", user.phone_num || "");
    setValue("email", user.email || "");
    setItsUserResults([]);
    setItsSearchTerm(user.username);
    document.activeElement instanceof HTMLElement &&
      document.activeElement.blur();
  };

  // Dialog handling for closing form
  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirm = () => {
    closeDialog();
    handleClose();
  };

  const handleClose = () => {
    clearErrors();
    reset({ ...initialValues, assigned_to: userInfo?.id || "" });
    setTitleInput("");
    setItsSearchTerm("");
    setOpen(false);
  };

  // Handle title selection
  const handleTitleSelect = (title: string) => {
    setSelectedTag(title);
    setValue("title", title, { shouldValidate: true });
    setTitleInput(title);
    setIsTitlePopoverOpen(false); // Close the popover after selection
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow spaces between words but normalize multiple spaces to single space
    const value = e.target.value.replace(/\s+/g, " ");
    setTitleInput(value);
    setValue("title", value);

    // If input doesn't match any tags, clear selected tag
    const selectedTagObj = tagOptions.find(t => t.tag_id === watch("tag_id"));
    const subTags = selectedTagObj?.tags || [];
    const isMatching = subTags.some(t => {
      const subTagName = typeof t === 'string' ? t : t.name;
      return subTagName === value.trim();
    });

    if (subTags.length > 0 && !isMatching) {
      setSelectedTag(null);
    }
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        ...initialValues,
        department_id: initialDepartment || "",
        service_id: initialService || "",
        title: initialTitle || "",
        counter_id: initialCounter || "",
        assigned_to: userInfo?.id || "",
        appointment_date: formatInTimeZone(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone, "yyyy-MM-dd"),
        appointment_time: `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`,
      });
      setSelectedDepartment(initialDepartment ? String(initialDepartment) : undefined);
      setSelectedService(initialService ? String(initialService) : undefined);
      setTitleInput(initialTitle || "");
    }
  }, [isOpen, initialDepartment, initialService, initialTitle, initialCounter, reset, userInfo?.id]);

  return (
    <Sheet open={isOpen}>
      <SheetContent
        hideCloseButton
        className="!max-w-[1050px] p-0 custom-scrollbar sm:w-[90%] sm:max-w-2xl"
      >
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-col gap-4 bg-gray-100 h-screen"
            noValidate
          >
            <SheetHeader className="sticky top-0 z-40 px-4 py-3 bg-white border-2 border-transparent border-b-gray-200">
              <SheetTitle>
                <div className="flex justify-between gap-5 items-center bg-white">
                  <p className="font-semibold">Create Ticket</p>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={openDialog}
                      className="w-[100px] gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted"
                    >
                      <i className="fa-regular fa-x text-xs font-semibold"></i>
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting || isGeneratingTicket}
                      disabled={isSubmitting || isGeneratingTicket}
                    >
                      {!isGeneratingTicket && !isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      )}
                      Save
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>

            <SheetDescription className="p-4 mx-4 bg-white rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                {/* ITS Number */}
                <div className="relative">
                  <div className="pb-2">
                    {/* <div className="flex flex-col gap-2 relative">
                      <label className="text-sm font-medium">Search by ITS</label> */}
                    <div className="relative ">
                      <span className="absolute p-0 text-xs left-12 -top-2 px-1 bg-white z-10">
                        Search by ITS <span className="text-red-500">*</span>
                      </span>
                      <div className="flex items-center relative w-auto">
                        <i className="fa-solid fa-magnifying-glass size-4 text-gray-400 absolute left-1 z-10 ml-2"></i>
                        <input
                          type="text"
                          className="p-2 pl-8 border rounded-md w-full"
                          value={itsSearchTerm}
                          onChange={(e) => setItsSearchTerm(e.target.value)}
                          placeholder="Search by ITS number"
                        />
                      </div>
                      {itsUserResults.length > 0 && (
                        <div className="absolute z-40 top-[70px] left-0 right-0 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {itsUserResults.map((user) => (
                            <div
                              key={user.username || user.id}
                              className="p-2 border-b last:border-none cursor-pointer hover:bg-gray-100 flex flex-col gap-1"
                              onClick={() => handleSelectItsUser(user)}
                            >
                              <div className="font-medium">{user.username}</div>
                              <div>
                                <span className="text-gray-500 text-sm font-normal capitalize">
                                  {user.first_name} {user.last_name}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <Controller
                  control={control}
                  name="path"
                  render={({ field }) => (
                    <>
                      <PathDropdown
                        value={field.value?.split('/').filter(Boolean).map(Number)}
                        onChange={(path) => {
                          if (!path || !path.length) return;
                          setValue("path", '/' + path.join('/'), { shouldValidate: true });
                          setValue("tag_id", path[path.length - 1]);
                        }}
                        groups={groupedTags}
                        label="Service"
                        placeholder="Select Service"
                        searchPlaceholder="Search tags..."
                        noResultsText="No Service found."
                        className="col-span-1 md:col-span-2"
                      />
                      <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                        {errors.tag_id?.message || ""}
                      </div>
                    </>
                  )}
                />
                {/* Title with Tags Dropdown */}
                <div className="relative col-span-1 md:col-span-3">
                  <div className="flex flex-col gap-2">
                    <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Popover
                        open={isTitlePopoverOpen}
                        onOpenChange={setIsTitlePopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <div className="relative w-full">
                            <Controller
                              control={control}
                              name="title"
                              rules={{ required: "Required" }}
                              render={({ field }) => (
                                <div
                                  className={`w-full h-10 px-3 py-2 border rounded-md cursor-pointer flex items-center justify-between ${errors.title
                                      ? "border-red-500"
                                      : "border-gray-300"
                                    }`}
                                  onClick={() => setIsTitlePopoverOpen(true)}
                                >
                                  <span className={titleInput ? "text-black" : "text-gray-400"}>
                                    {titleInput || "Select a title"}
                                  </span>
                                  <ChevronDownIcon className="h-4 w-4 opacity-50" />
                                </div>
                              )}
                            />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent 
                          className="p-0 w-[--radix-popover-trigger-width] overflow-hidden" 
                          align="start"
                          onWheel={(e) => e.stopPropagation()}
                        >
                          {!watch("tag_id") ? (
                            <div className="p-4 text-sm text-center text-muted-foreground">
                              Please select a tag first
                            </div>
                          ) : tagOptions &&
                            tagOptions.length > 0 ? (
                            <Command className="w-full">
                              <CommandInput placeholder="Search titles..." className="h-9" />
                              <CommandList 
                                className="max-h-[300px] overflow-y-auto custom-scrollbar"
                                onWheel={(e) => e.stopPropagation()}
                              >
                                  <CommandEmpty>No title found.</CommandEmpty>
                                  <CommandGroup>
                                    {tagOptions
                                      .filter((tag: any) => tag.tag_id === watch("tag_id"))
                                      .map((parentTag: any) => (
                                        <React.Fragment key={parentTag.tag_id}>
                                          {parentTag.tags && parentTag.tags.length > 0 && (
                                            parentTag.tags.map((subTag: any, index: number) => {
                                              const subTagName = typeof subTag === 'string' ? subTag : subTag.name;
                                              return (
                                                <CommandItem
                                                  key={`${parentTag.tag_id}-${index}`}
                                                  onSelect={() => handleTitleSelect(subTagName)}
                                                  className="cursor-pointer"
                                                >
                                                  <Check
                                                    className={cn(
                                                      "mr-2 h-4 w-4",
                                                      watch("title") === subTagName
                                                        ? "opacity-100"
                                                        : "opacity-0"
                                                    )}
                                                  />
                                                  {subTagName}
                                                </CommandItem>
                                              );
                                            })
                                          )}
                                        </React.Fragment>
                                      ))}
                                  </CommandGroup>
                              </CommandList>
                            </Command>
                          ) : (
                            <div className="p-4 text-sm text-center">
                              No tags available
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                      <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                        {errors.title?.message || ""}
                      </div>
                    </div>
                  </div>
                </div>


                {/* Description */}
                <div className="relative col-span-1 md:col-span-3">
                  <FloatingLabelInput
                    id="description"
                    placeholder="Enter Description"
                    type="textarea"
                    rows={4}
                    maxLength={250}
                    {...form.register("description", {
                      maxLength: {
                        value: 250,
                        message: "Should be 250 characters or less",
                      },
                    })}
                    error={errors.description?.message}
                  />
                </div>

                {/* Customer Name */}
                <div className="relative">
                  <FloatingLabelInput
                    id="customer_name"
                    label="Customer Name"
                    className="capitalize w-full"
                    {...form.register("customer_name")}
                    disabled={true} // Auto-populated
                  />
                </div>

                {/* Phone Number */}
                <div className="relative">
                  <Controller
                    control={control}
                    name="phone_number"
                    render={({ field }) => (
                      <div className="relative ">
                        <span className="absolute p-0 text-xs left-12 -top-2 px-1 bg-white z-10">
                          Phone Number <span className="text-red-500">*</span>
                        </span>
                        <PhoneInputField
                          value={field.value || ""}
                          onChange={(value) => field.onChange(value)}
                          disabled={true}
                        />
                      </div>
                    )}
                  />
                </div>

                {/* Email */}
                <div className="relative">
                  <FloatingLabelInput
                    id="email"
                    label="Email"
                    className="w-full"
                    {...form.register("email")}
                    disabled={true} // Auto-populated
                  />
                </div>
                {/* Status */}
                <div className="relative">
                  <Controller
                    control={control}
                    name="status"
                    rules={{ required: "Required" }}
                    render={({ field: { onChange, value } }) => {
                      // Check if selected date is in the future
                      const selectedDate = watch("appointment_date");
                      // Always use NEW status by default
                      const statusValue = "NEW";

                      // Auto-set the status value based on date
                      React.useEffect(() => {
                        if (selectedDate) {
                          onChange(statusValue);
                        }
                      }, [selectedDate, onChange, statusValue]);

                      return (
                        <>
                          <div className="relative w-full">
                            <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                              Status
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="p-2 border rounded-md bg-gray-50 text-sm capitalize">
                              {statusValue}
                              <span className="text-xs italic ml-2">
                                (Automatically set to NEW)
                              </span>
                            </div>
                          </div>
                          <div className="h-[20px] text-red-500 text-xs mt-[5px]">
                            {errors.status?.message || ""}
                          </div>
                        </>
                      );
                    }}
                  />
                </div>

                {/* Assigned To */}
                <div className="relative">
                  <Controller
                    control={control}
                    name="assigned_to"
                    render={({ field: { onChange, value } }) => (
                      <>
                        <div className="relative w-full">
                          <label className="absolute text-xs font-medium left-2 -top-2 z-10 bg-white px-1 text-gray-600">
                            Assignee
                          </label>
                          <Popover modal={true}>
                            <PopoverTrigger asChild >
                              <Button className="capitalize w-full scale-100 shadow-sm border-checkboxborder border-[1px] bg-white text-gray-600 hover:bg-white">
                                <span className="w-full text-left font-normal">
                                  {value
                                    ? staffOptions?.find(
                                        (staff) =>
                                          String(staff.value) === String(value)
                                      )?.label || "Select Assignee"
                                    : "Select Assignee"}
                                </span>
                                <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 relative"
                              side="bottom"
                            >
                              <Command className="w-full">
                                <CommandList>
                                  <CommandInput placeholder="Search Staff..." />
                                  <CommandEmpty>
                                    No Khidmat Guzaar found.
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {staffOptions?.map((staff) => (
                                      <CommandItem
                                        value={String(staff.label)}
                                        key={staff.value}
                                        className="capitalize"
                                        onSelect={() => onChange(staff.value)}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                            String(staff.value) ===
                                              String(value)
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {staff.label}
                                        {staff.designation && (
                                          
                                          <span className="text-xs text-gray-500 ml-2">
                                            {staff.designation}
                                          </span>
                                        )}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </>
                    )}
                  />
                </div>

              {paramEventId && (
                <>
                  {/* Appointment Date */}
                  <div className="relative">
                    <FloatingLabelInput
                      id="appointment_date"
                      type="date"
                      label="Appointment Date"
                      text="*"
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full"
                      {...form.register("appointment_date", {
                        required: "Required",
                        value: formatInTimeZone(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone, "yyyy-MM-dd"),
                      })}
                      error={errors.appointment_date?.message}
                    />
                  </div>

                  {/* Appointment Time */}
                  <div className="relative">
                    <FloatingLabelInput
                      id="appointment_time"
                      type="time"
                      label="Appointment Time"
                      text="*"
                      className="w-full"
                      {...form.register("appointment_time", {
                        required: "Required",
                        value: `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`,
                        validate: (value) => {
                          const selectedDate = watch("appointment_date");
                          // Only validate time if date is today
                          if (
                            selectedDate ===
                            new Date().toISOString().split("T")[0]
                          ) {
                            const now = new Date();
                            const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
                            return (
                              value >= currentTime ||
                              "Time must be in equal to now or greater"
                            );
                          }
                          return true;
                        },
                      })}
                      error={errors.appointment_time?.message}
                    />
                  </div>
                </>
              )}
              </div>
            </SheetDescription>

            <ConfirmationDialog
              isOpen={isDialogOpen}
              onClose={closeDialog}
              onConfirm={handleConfirm}
            />
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default AddTicketForm;
